import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdZoomIn } from 'react-icons/md';
import { THEME, SPRING } from '../theme';

interface SwatchPreviewModalProps {
  isOpen: boolean;
  imageUrl: string;
  label?: string;
  onClose: () => void;
}

const SwatchPreviewModal: React.FC<SwatchPreviewModalProps> = ({ isOpen, imageUrl, label, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          onClick={onClose}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.6, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.6, opacity: 0, y: 20 }}
            transition={SPRING.snappy}
            onClick={(e) => e.stopPropagation()}
            className="relative z-10 max-w-[420px] w-[90vw] rounded-3xl overflow-hidden"
            style={{
              background: 'rgba(20, 20, 26, 0.95)',
              border: `1px solid ${THEME.primaryBorder}`,
              boxShadow: `0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(139, 92, 246, 0.15)`,
            }}
          >
            {/* Image */}
            <div className="relative w-full aspect-square overflow-hidden">
              <img
                src={imageUrl}
                alt={label || 'Fabric preview'}
                className="w-full h-full object-cover"
              />
              {/* Gradient overlay at bottom */}
              <div
                className="absolute inset-x-0 bottom-0 h-24"
                style={{
                  background: 'linear-gradient(to top, rgba(20, 20, 26, 0.95) 0%, transparent 100%)',
                }}
              />

              {/* Zoom indicator */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full"
                style={{
                  background: 'rgba(0,0,0,0.5)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <MdZoomIn size={14} style={{ color: THEME.primaryLight }} />
                <span className="text-[10px] font-semibold text-white/70">Detail View</span>
              </motion.div>
            </div>

            {/* Label bar */}
            {label && (
              <div className="px-5 py-4 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em] block mb-0.5" style={{ color: THEME.primaryLight }}>
                    Material
                  </span>
                  <span className="text-sm font-semibold text-white">{label}</span>
                </div>
                <div
                  className="w-10 h-10 rounded-xl overflow-hidden"
                  style={{ border: `2px solid ${THEME.primaryBorder}` }}
                >
                  <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
            )}

            {/* Close button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-colors"
              style={{
                background: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.15)',
              }}
            >
              <MdClose size={18} className="text-white/80" />
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SwatchPreviewModal;
